import React from 'react';
import { services } from '../mockData';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our Services
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Comprehensive food manufacturing solutions from recipe to retail
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-96 object-cover rounded-none shadow-xl hover:shadow-2xl transition-all duration-300"
                  />
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="bg-emerald-600 text-white text-sm font-bold px-4 py-2 rounded-none inline-block mb-4">
                    Service {service.id}
                  </div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <CheckCircle size={24} className="text-emerald-600 flex-shrink-0" />
                        <span className="text-gray-800 text-lg">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-emerald-600 text-white px-8 py-4 rounded-none font-semibold transition-all duration-300 hover:bg-emerald-700 hover:shadow-xl hover:-translate-y-1"
                  >
                    Learn More
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Additional Support Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive support beyond manufacturing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Partner Support</h3>
              <p className="text-gray-700 mb-4">
                Guidance for retail, D2C, and foodservice distribution channels to help you reach your target market effectively.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Retail placement support</span>
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Online marketplace setup</span>
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Foodservice partnerships</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Regulatory Compliance</h3>
              <p className="text-gray-700 mb-4">
                Full support for UK and EU food regulations, ensuring your products meet all legal requirements.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Labeling compliance</span>
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Allergen management</span>
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Documentation support</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Brand Development</h3>
              <p className="text-gray-700 mb-4">
                Strategic guidance to help position your brand and products in the competitive food market.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Market positioning</span>
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Product packaging advice</span>
                </li>
                <li className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={18} className="text-blue-600" />
                  <span>Launch strategy support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
            Let's discuss your manufacturing needs and create a tailored solution for your brand
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-emerald-600 px-10 py-5 rounded-none font-bold text-lg transition-all duration-300 hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1"
          >
            Contact Us Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
